import { intersection, uniq, flatten } from 'lodash'
import {
  ALL_GRADES,
  ASSETS_MAP,
  FLORIDA,
  GRADE_ASSETS,
  GRADE_PROGRAMS,
  HIDE_HOME_BUTTON,
  HIDE_STUDENT_BACKPACK_MENU,
  HUB_ASSET_BUSINESS_KEY,
  HUB_PROGRAMS,
  HUB_PROGRAMS_OVERRIDES,
  MIDDLESCHOOL_GRADES,
  READERS_MAP,
  SKILLS_2020,
  SKILLS_2021,
  STUDENT_ROLES,
  TEACHER_RESOURCE_SITE_ASSET,
  TEACHER_ROLES,
} from 'app/constants/entitlement.constant'
import { LOCAL_STORAGE_KEYS } from 'app/constants/local-storage.constant'
import {
  IS_ALL_APPS_FILTER_AVAILABLE,
  PROGRAM_FILTER_PROGRAM_MAP,
  PROGRAM_FILTERS,
} from 'app/constants/home-links.constants'
import { Asset, UserProgram } from 'app/types/asset.types'
import { Readers, UserPermissions } from 'app/types/auth.types'
import { GRADES } from 'app/types/grades.types'
import { ProgramFilter } from 'app/types/home-content-button.types'
import { arrayContainsItemFromAnother } from 'app/utils/array'
import { RootState } from 'app/store'

type PageAccessCheckerGetterParams = {
  allowedPrograms: HUB_PROGRAMS[]
  allowedGrades?: GRADES[]
  checkAllPrograms?: boolean
}

type AccessCheckParams = Omit<
  PageAccessCheckerGetterParams,
  'checkAllPrograms'
> & {
  appState: RootState
}

const gradesComparator = (a: GRADES, b: GRADES) => {
  if (a === GRADES.KINDERGARTEN) return -1
  if (b === GRADES.KINDERGARTEN) return 1
  return +a - +b
}

const checkIfAssetIsPresentByBk = (assets: Asset[], assetToFindBk: string) => {
  const assetsByKey = assets.filter(
    (asset) => asset.businessKey === assetToFindBk
  )
  return assetsByKey.length > 0
}

const isEntitled = (assets: Asset[]) =>
  checkIfAssetIsPresentByBk(assets, HUB_ASSET_BUSINESS_KEY)

const isValidAsset = (asset: Asset) =>
  flatten(Object.values(GRADE_ASSETS)).some((element) =>
    asset.name?.includes(element)
  )

const getUserPermissions = (assets: Asset[]): UserPermissions =>
  assets.reduce((permissions: UserPermissions, asset: Asset) => {
    if (ASSETS_MAP[asset.businessKey])
      return [...permissions, ASSETS_MAP[asset.businessKey]] as UserPermissions
    return permissions
  }, [] as UserPermissions)

const getReaders = (assets: Asset[]): Readers =>
  assets.reduce((readers: Readers, asset: Asset) => {
    if (READERS_MAP[asset.businessKey])
      return [...readers, READERS_MAP[asset.businessKey]] as Readers
    return readers
  }, [] as Readers)

export const has2021Readers = (readers: Readers) =>
  readers.includes(SKILLS_2021)

export const has2020Readers = (readers: Readers) =>
  readers.includes(SKILLS_2020)

export const isTeacherResourceSite = (permissions: UserPermissions) =>
  permissions.includes(TEACHER_RESOURCE_SITE_ASSET)

export const isFlorida = (permissions: UserPermissions) =>
  permissions.includes(FLORIDA)

export const isHomeButtonHidden = (permissions: UserPermissions) =>
  permissions.includes(HIDE_HOME_BUTTON)

export const isStudentBackpackMenuHidden = (permissions: UserPermissions) =>
  permissions.includes(HIDE_STUDENT_BACKPACK_MENU)

export const getHubPrograms = (
  assets: Asset[],
  userPrograms?: UserProgram[]
) => {
  const hubProgramsByGrade: Record<string, GRADES[]> = {}

  const gradePrograms = Object.keys(GRADE_ASSETS)
  gradePrograms.forEach((program) => {
    hubProgramsByGrade[program] = []
    GRADE_ASSETS[program as keyof typeof GRADE_ASSETS].forEach((gradeAsset) => {
      assets.forEach((asset) => {
        if (asset.name?.includes(gradeAsset) && asset.metadata?.grade) {
          hubProgramsByGrade[program].push(asset.metadata.grade)
        }
      })
    })
    hubProgramsByGrade[program] = uniq(hubProgramsByGrade[program])
  })

  const programs = Object.keys(GRADE_PROGRAMS)
  programs.forEach((program) => {
    hubProgramsByGrade[program] = []
    GRADE_PROGRAMS[program as keyof typeof GRADE_PROGRAMS].forEach(
      (gradeProgram) => {
        userPrograms?.forEach((userProgram) => {
          if (userProgram.name?.includes(gradeProgram)) {
            hubProgramsByGrade[program].push(...ALL_GRADES)
          }
        })
      }
    )
    hubProgramsByGrade[program] = uniq(hubProgramsByGrade[program])
  })

  Object.keys(HUB_PROGRAMS_OVERRIDES).forEach((overriddenProgram) => {
    const overridingProgram =
      HUB_PROGRAMS_OVERRIDES[
        overriddenProgram as keyof typeof HUB_PROGRAMS_OVERRIDES
      ]
    // filter out all the grades which have both overriding and overridden programs
    hubProgramsByGrade[overriddenProgram] = hubProgramsByGrade[
      overriddenProgram
    ].filter((grade) => !hubProgramsByGrade[overridingProgram].includes(grade))
  })

  return hubProgramsByGrade
}

export const isUserEntitledToGradeForProgram = (
  grade: string,
  program: string,
  hubPrograms: Record<string, string[]>
) => hubPrograms[program]?.includes(grade) ?? false

const getGradesFromAssets = (assets: Asset[]) => {
  const grades: GRADES[] = []
  assets.forEach((asset) => {
    if (isValidAsset(asset) && asset.metadata?.grade) {
      grades.push(asset.metadata.grade)
    }
  })
  return [...new Set(grades)].sort(gradesComparator)
}

const getDefaultGrade = (grades: GRADES[]) =>
  grades.sort(gradesComparator)[grades.length - 1] || GRADES.KINDERGARTEN

export const getSortedGrades = (grades: GRADES[]) =>
  grades.sort(gradesComparator)

export const getGradesUpToHighestGrade = (grade: GRADES) => {
  const allGrades = getSortedGrades(Object.values(GRADES))
  return allGrades.slice(0, allGrades.indexOf(grade) + 1)
}

const getCurrentGrade = (grades: GRADES[]) => {
  let selectedGrade = localStorage.getItem(LOCAL_STORAGE_KEYS.GRADE) as GRADES
  if (!selectedGrade) selectedGrade = getDefaultGrade(grades)
  return selectedGrade
}

const isMiddleSchool = (grades: GRADES[]) => {
  // for support both string and num
  const MSGradesIntArr = MIDDLESCHOOL_GRADES.map((grade) => +grade)
  return grades.some((grade) => MSGradesIntArr.includes(+grade))
}

const isTeacherInRoles = (roles?: string[]) =>
  roles && arrayContainsItemFromAnother(TEACHER_ROLES, roles)

const isStudentInRoles = (roles?: string[]) =>
  roles && arrayContainsItemFromAnother(STUDENT_ROLES, roles)

const getTeacherGuideAccessCheck = () => (appState: RootState) =>
  Boolean(appState.userData.kcInfo.isTeacher)

/**
 * Returns true if user is entitled to at ALL allowed programs
 * Entitlement fact is based on grades presence.
 * At least one grade or one from specific grades set, if allowedGrades is passed,
 * should be present for ALL allowed programs.
 * Search is performed in hubPrograms from userData stored in the app state.
 * */
const performFullCheck = ({
  allowedPrograms,
  appState,
  allowedGrades,
}: AccessCheckParams) => {
  const allowedProgramsWithData = allowedPrograms.filter((program) =>
    Boolean(appState.userData.hubPrograms[program].length)
  )

  if (allowedProgramsWithData.length !== allowedPrograms.length) return false

  if (allowedGrades) {
    return allowedProgramsWithData.every((programName) => {
      const programGrades = appState.userData.hubPrograms[programName]
      return Boolean(intersection(programGrades, allowedGrades).length)
    })
  }

  return true
}

/**
 * Returns true if user is entitled to at least ONE allowed program
 * At least one grade or one from specific grades set, if allowedGrades is passed,
 * should be present for at least ONE of the allowed programs.
 * Search is performed in hubPrograms from userData stored in the app state.
 * */
const performPartialCheck = ({
  allowedPrograms,
  appState,
  allowedGrades,
}: AccessCheckParams) => {
  const allowedProgramsWithData = allowedPrograms.filter((program) =>
    Boolean(appState.userData.hubPrograms[program].length)
  )

  if (allowedGrades) {
    return allowedProgramsWithData.some((programName) => {
      const programGrades = appState.userData.hubPrograms[programName]

      return Boolean(intersection(programGrades, allowedGrades).length)
    })
  }

  return Boolean(allowedProgramsWithData.length)
}

const getPageAccessChecker =
  ({
    allowedPrograms,
    allowedGrades,
    checkAllPrograms = true,
  }: PageAccessCheckerGetterParams) =>
  (appState: RootState) => {
    if (!checkAllPrograms) {
      return performPartialCheck({ allowedPrograms, appState, allowedGrades })
    }

    return performFullCheck({ allowedPrograms, appState, allowedGrades })
  }

export const getFilters = (
  grades: GRADES[],
  hubPrograms: Record<string, string[]>
): Record<string, Array<ProgramFilter>> => {
  const filters: Record<string, Array<ProgramFilter>> = {}
  const programFiltersKeys = Object.keys(PROGRAM_FILTERS)

  grades.forEach((grade) => {
    programFiltersKeys.forEach((filter) => {
      if (
        isUserEntitledToGradeForProgram(
          grade,
          PROGRAM_FILTER_PROGRAM_MAP[filter],
          hubPrograms
        )
      ) {
        filters[grade] = filters[grade] || []
        filters[grade].push(filter)
      }
    })
    if (filters[grade] && IS_ALL_APPS_FILTER_AVAILABLE) {
      filters[grade].unshift(PROGRAM_FILTERS.ALL_APPS)
    }
  })

  return filters
}

const EntitlementService = {
  getGradesFromAssets,
  isEntitled,
  getDefaultGrade,
  getCurrentGrade,
  isMiddleSchool,
  isTeacherInRoles,
  isStudentInRoles,
  getUserPermissions,
  checkIfAssetIsPresentByBk,
  getReaders,
  getHubPrograms,
  isUserEntitledToGradeForProgram,
  getTeacherGuideAccessCheck,
  getPageAccessChecker,
  getFilters,
}

export default EntitlementService
